import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, CreditCard, Loader2, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractCompletionPage() {
  const [searchParams] = useSearchParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState('');
  const contractId = searchParams.get('contract_id');

  useEffect(() => {
    const loadContract = async () => {
      if (!contractId) {
        setError('No encontramos el contrato que debe continuar al pago.');
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(`${API}/contracts/public-status/${contractId}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'No se pudo cargar el estado del contrato');
        setContract(data.contract || null);
      } catch (err) {
        setError(err.message || 'No se pudo cargar el contrato.');
      } finally {
        setLoading(false);
      }
    };
    loadContract();
  }, [contractId]);

  const paymentLabel = useMemo(() => {
    if (!contract?.payment_package?.price) return null;
    return `$${contract.payment_package.price} ${String(contract.payment_package.currency || 'usd').toUpperCase()}`;
  }, [contract]);

  const handleCheckout = async () => {
    if (!contractId) return;
    setCheckoutLoading(true);
    setError('');
    try {
      const response = await fetch(`${API}/contracts/${contractId}/payment-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo abrir el checkout');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || 'No se pudo abrir el checkout.');
      setCheckoutLoading(false);
    }
  };

  return (
    <>
      <SEO title="Contrato firmado | Continuar al pago" description="Continúa al pago tras completar la firma del contrato." path="/firma-completada" />
      <main className="min-h-screen bg-background px-4 py-16 sm:px-6 lg:px-8" data-testid="contract-completion-page">
        <div className="mx-auto max-w-4xl">
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="glass-card tracing-panel p-8 md:p-10" data-testid="contract-completion-card">
            <p className="editorial-kicker mb-4">Contrato completado</p>
            <h1 className="text-4xl sm:text-5xl text-text-heading" data-testid="contract-completion-title">Tu firma ya quedó registrada. Ahora puedes continuar al pago.</h1>
            <p className="mt-4 max-w-2xl text-text-body text-lg" data-testid="contract-completion-subtitle">Te dejamos una ruta clara y corta para pasar de la firma a la activación económica sin depender de enlaces largos ni pasos difusos.</p>

            {loading ? (
              <div className="mt-8 flex items-center gap-3 text-text-body" data-testid="contract-completion-loading"><Loader2 className="w-5 h-5 animate-spin text-primary" /> Cargando estado del contrato…</div>
            ) : error ? (
              <div className="mt-8 rounded-[22px] border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200" data-testid="contract-completion-error">{error}</div>
            ) : (
              <div className="mt-8 grid gap-5 lg:grid-cols-[1.15fr_0.85fr]">
                <div className="rounded-[26px] border border-border-subtle bg-background/55 p-6" data-testid="contract-completion-status-card">
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-primary/10 p-3"><CheckCircle2 className="w-5 h-5 text-primary" /></div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-text-muted">Estado actual</p>
                      <h2 className="mt-2 text-2xl text-text-heading">{contract?.name || 'Contrato firmado'}</h2>
                      <p className="mt-2 text-sm text-text-body">Estado SignWell: <span className="text-text-heading font-medium">{contract?.signwell_status || contract?.status || 'pendiente'}</span></p>
                    </div>
                  </div>

                  {contract?.payment_package ? (
                    <div className="mt-6 rounded-[22px] border border-primary/20 bg-primary/5 p-5" data-testid="contract-completion-payment-package-card">
                      <p className="text-xs uppercase tracking-[0.18em] text-text-muted">Siguiente paso</p>
                      <h3 className="mt-2 text-xl text-text-heading">{contract.payment_package.name}</h3>
                      <p className="mt-2 text-text-body">{contract.payment_package.description}</p>
                      {paymentLabel && <p className="mt-3 text-lg font-semibold text-primary" data-testid="contract-completion-payment-price">{paymentLabel}</p>}
                    </div>
                  ) : (
                    <div className="mt-6 rounded-[22px] border border-border-subtle bg-background/45 p-5 text-sm text-text-body" data-testid="contract-completion-no-payment-card">
                      Este contrato no tiene un pago enlazado todavía. Si necesitas activarlo, díselo al equipo de Neuralia369.
                    </div>
                  )}
                </div>

                <div className="rounded-[26px] border border-border-subtle bg-background/55 p-6" data-testid="contract-completion-action-card">
                  <p className="text-xs uppercase tracking-[0.18em] text-text-muted">Acción inmediata</p>
                  <h3 className="mt-2 text-2xl text-text-heading">Continúa sin fricción</h3>
                  <p className="mt-3 text-sm text-text-body">Si tu firma ya está completa y el contrato quedó listo para cobro, aquí mismo puedes abrir el checkout correspondiente.</p>

                  <button type="button" onClick={handleCheckout} disabled={!contract?.ready_for_payment || checkoutLoading} className="btn-primary mt-6 w-full justify-center" data-testid="contract-completion-pay-button">
                    {checkoutLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Abriendo checkout…</> : <><CreditCard className="w-4 h-4" /> {contract?.payment_cta_label || 'Proceder al pago'}</>}
                  </button>

                  <Link to="/conectar-google" className="btn-secondary mt-3 w-full justify-center" data-testid="contract-completion-google-button">
                    <Sparkles className="w-4 h-4" /> Conectar Google sin enlace largo
                  </Link>

                  <p className="mt-4 text-xs text-text-muted">Si acabas de firmar y todavía no ves el botón activo, espera unos segundos y recarga esta página.</p>
                </div>
              </div>
            )}

            <div className="mt-8">
              <Link to="/" className="btn-secondary inline-flex" data-testid="contract-completion-home-link">Volver al inicio <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </motion.section>
        </div>
      </main>
    </>
  );
}